import { NotificationGroups, PersistedNotification } from "@/types/notification"

import { now } from "@/lib/date-utils"
import { PagingMeta } from "@/lib/paging"
import { useAppSelector } from "../hooks"
import { selectUserIsSignedIn } from "../user-slice"
import { baseApi } from "./base-api"

const NOTIFICATIONS_PER_PAGE = 15

export interface FetchNotificationParams {
  page?: number
  perPage?: number
  group?: NotificationGroups
  unreadOnly?: boolean
}

export interface FetchNotificationsResponse {
  notifications: PersistedNotification[]
  meta: PagingMeta
  all_unread: {
    count: number
    latest_id?: number
  }
}

export interface UnreadResponse {
  count: number
  latest_id?: number
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      FetchNotificationsResponse,
      FetchNotificationParams
    >({
      query: (args) => {
        const {
          page = 1,
          perPage = NOTIFICATIONS_PER_PAGE,
          group,
          unreadOnly,
        } = args
        const urlQuery = new URLSearchParams()
        urlQuery.set("page", page.toString())
        urlQuery.set("per_page", perPage.toString())
        if (group) {
          urlQuery.set("group", group)
        }
        if (unreadOnly) {
          urlQuery.set("unread_only", "true")
        }
        return {
          url: `/api/notifications?${urlQuery.toString()}`,
        }
      },
      providesTags: ["Notifications"],
      serializeQueryArgs: ({ queryArgs }) => {
        // Cache key based on group and unreadOnly, NOT page
        // This allows pagination to share the same cache entry
        return JSON.stringify({
          group: queryArgs.group,
          unreadOnly: queryArgs.unreadOnly,
        })
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results - append pages after page 1
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            notifications: [
              ...current.notifications,
              ...incoming.notifications,
            ],
          }
        }
        // For page 1 (or no page specified): replace cache with fresh data
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        // Only refetch when a component changes its page arg (pagination)
        return currentArg?.page !== previousArg?.page
      },
      keepUnusedDataFor: 300,
    }),

    getUnread: builder.query<UnreadResponse, void>({
      query: (_arg) => ({
        url: "/api/notifications/unread",
      }),
      providesTags: ["UnreadCount"],
      keepUnusedDataFor: 300,
    }),

    markRead: builder.mutation<PersistedNotification, number>({
      query: (id) => ({
        url: `/api/notifications/${id}/mark-read`,
        method: "POST",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Optimistic update: mark as read immediately in cache
        const patches: Array<{ undo: () => void }> = []

        // Update all cached notification queries
        const cacheKeys = [
          JSON.stringify({ group: undefined, unreadOnly: undefined }),
          JSON.stringify({ group: undefined, unreadOnly: false }),
          JSON.stringify({ group: "mentions", unreadOnly: undefined }),
          JSON.stringify({ group: "mentions", unreadOnly: false }),
          JSON.stringify({ group: "actions", unreadOnly: undefined }),
          JSON.stringify({ group: "actions", unreadOnly: false }),
        ]

        cacheKeys.forEach((cacheKey) => {
          try {
            const patch = dispatch(
              notificationsApi.util.updateQueryData(
                "getNotifications",
                JSON.parse(cacheKey) as FetchNotificationParams,
                (draft) => {
                  const notification = draft.notifications.find(
                    (n) => n.id === id
                  )
                  if (notification && !notification.read_at) {
                    notification.read_at = now()
                    draft.all_unread.count = Math.max(
                      0,
                      draft.all_unread.count - 1
                    )
                  }
                }
              )
            )
            patches.push(patch)
          } catch {
            // Cache entry doesn't exist, ignore
          }
        })

        // Also update unread count
        const unreadPatch = dispatch(
          notificationsApi.util.updateQueryData(
            "getUnread",
            undefined,
            (draft) => {
              draft.count = Math.max(0, draft.count - 1)
            }
          )
        )
        patches.push(unreadPatch)

        try {
          await queryFulfilled
        } catch {
          // Revert optimistic updates on error
          patches.forEach((patch) => patch.undo())
        }
      },
      // Don't invalidate tags - optimistic update already has correct data
    }),

    markAllRead: builder.mutation<void, number>({
      query: (lastReadId) => ({
        url: "/api/notifications/mark-all-read",
        method: "POST",
        data: { last_read_id: lastReadId },
      }),
      transformResponse: () => undefined,
      async onQueryStarted(_lastReadId, { dispatch, queryFulfilled }) {
        // Optimistic update: mark all as read immediately
        const patches: Array<{ undo: () => void }> = []
        const timestamp = now()

        // Update all cached notification queries
        const cacheKeys = [
          JSON.stringify({ group: undefined, unreadOnly: undefined }),
          JSON.stringify({ group: undefined, unreadOnly: false }),
          JSON.stringify({ group: "mentions", unreadOnly: undefined }),
          JSON.stringify({ group: "mentions", unreadOnly: false }),
          JSON.stringify({ group: "actions", unreadOnly: undefined }),
          JSON.stringify({ group: "actions", unreadOnly: false }),
        ]

        cacheKeys.forEach((cacheKey) => {
          try {
            const patch = dispatch(
              notificationsApi.util.updateQueryData(
                "getNotifications",
                JSON.parse(cacheKey) as FetchNotificationParams,
                (draft) => {
                  draft.notifications.forEach((n) => {
                    if (!n.read_at) {
                      n.read_at = timestamp
                    }
                  })
                  draft.all_unread.count = 0
                }
              )
            )
            patches.push(patch)
          } catch {
            // Cache entry doesn't exist, ignore
          }
        })

        // Also update unread count
        const unreadPatch = dispatch(
          notificationsApi.util.updateQueryData(
            "getUnread",
            undefined,
            (draft) => {
              draft.count = 0
            }
          )
        )
        patches.push(unreadPatch)

        try {
          await queryFulfilled
        } catch {
          // Revert optimistic updates on error
          patches.forEach((patch) => patch.undo())
        }
      },
      // Invalidate unread-only views since they'll be empty now
      invalidatesTags: ["Notifications"],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
} = notificationsApi

// The one way to read the unread count in a component. An anonymous session
// can never be sent a notification, so it skips the fetch and reads zero.
export function useUnreadNotificationCount(): number {
  const isSignedIn = useAppSelector(selectUserIsSignedIn)
  const { count } = useGetUnreadQuery(undefined, {
    skip: !isSignedIn,
    selectFromResult: ({ data }) => ({ count: data?.count ?? 0 }),
  })
  return count
}

export const selectLatestNotificationId = (state: {
  api: ReturnType<typeof baseApi.reducer>
}) => {
  const result = notificationsApi.endpoints.getUnread.select()(state)
  return result.data?.latest_id
}
